import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchCustomerHealthDetail, updateCustomerHealth, insertActionItem, updateActionItemStatus,
  insertActionItemComment, upsertProduct, insertDeal, updateDeal, insertChangeLog, CustomerHealthPatch,
} from '../../services/customerHealthService';
import { fetchBrandsByUltimateParent, BrandData } from '../../services/brandService';
import { useUsersLookup } from '../../hooks/useUsersLookup';
import { useRoles } from '../../hooks/useRoles';
import useAuthStore from '../../stores/authStore';
import UserPicker from '../../components/UserPicker/UserPicker';
import CustomDropdownWithSearch from '../../components/CustomDropdownWithSearch/CustomDropdownWithSearch';
import {
  ActionItem, ActionStatus, CustomerHealthDetail, Deal, DealStatus, HealthStatus, HistoryEntry, Metric,
} from './types';
import { healthStatusMeta, dealStatusMeta, actionStatusMeta, budgetToneFromBacklogPct, forecastToneFromPct, avatarBg, ALL_PRODUCTS } from './meta';
import { money, pctOf, shortDate, shortTimestamp, initialsOf, userDisplayName, canEditChRecords, openDealsOf, displayProductsFor, DisplayProduct } from './helpers';
import { Dot, Badge } from './components/primitives';
import { ActionItemRow } from './components/ActionItemRow';

// temp/money edit `path` -> the customer_health column each writes.
const TEMP_COLUMN: Record<string, keyof CustomerHealthPatch> = { trackStatus: 'track_status', sentimentStatus: 'sentiment_status', budgetStatus: 'budget_status' };
const MONEY_COLUMN: Record<string, keyof CustomerHealthPatch> = { backlog: 'backlog_amount', annualBudget: 'annual_budget', forecast: 'forecast_amount' };

interface EditConfig {
  kind: 'temp' | 'status' | 'text' | 'money' | 'newAction' | 'hero' | 'product' | 'deal';
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initial?: any;
  path?: string;          // temp: trackStatus|sentimentStatus|budgetStatus ; money: backlog|annualBudget|forecast
  actionId?: string;      // status
  productKey?: string;    // product
  dealId?: string | null; // deal (null = add new)
}

const NOTE_KINDS = ['temp', 'status', 'text', 'money', 'newAction', 'hero'];

/**
 * Customer Health — brand / ultimate-parent detail page.
 * Route: `/dashboard/customer-health/:parentId` (parentId = exact ultimate_parent, URI-encoded).
 * Fetches its own scoped record; owns every inline-edit and re-fetches the record after each save.
 */
function BrandHealthDetail() {
  const navigate = useNavigate();
  const { parentId } = useParams();
  const ultimateParent = parentId ? decodeURIComponent(parentId) : '';
  const { users, byId } = useUsersLookup();
  const role = useAuthStore((s) => s.user?.role);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { roles } = useRoles();
  const currentUserHierarchy = roles.find((r) => r.name === role)?.role_hierarchy;
  const canEditRecords = canEditChRecords(currentUserHierarchy);
  const displayName = (idOrLegacy: string) => userDisplayName(byId, idOrLegacy);

  const [detail, setDetail] = useState<CustomerHealthDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // edit modal
  const [editing, setEditing] = useState<EditConfig | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draft, setDraft] = useState<any>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [brandOptions, setBrandOptions] = useState<BrandData[] | null>(null);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);

  const load = useCallback(async (silent?: boolean) => {
    if (!silent) { setLoading(true); setError(null); }
    try {
      const rec = await fetchCustomerHealthDetail(ultimateParent);
      setDetail(rec);
    } catch {
      if (!silent) setError('Failed to load this ultimate parent.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [ultimateParent]);

  useEffect(() => { load(); }, [load]);

  // Brands under this ultimate parent, for the deal modal's Brand picker.
  useEffect(() => {
    if (editing?.kind !== 'deal' || !detail) return;
    setBrandOptionsLoading(true);
    setBrandOptions(null);
    fetchBrandsByUltimateParent(detail.ultimateParent.toUpperCase())
      .then((brands) => setBrandOptions(brands))
      .catch(() => setBrandOptions([]))
      .finally(() => setBrandOptionsLoading(false));
  }, [editing?.kind, detail]);

  const back = () => navigate('/dashboard/customer-health');

  /* ---------- editing ---------- */
  const startEdit = (cfg: EditConfig) => {
    if (!canEditRecords) return;
    setEditing(cfg); setDraft(cfg.initial); setNote(''); setSaveError(null);
  };
  const cancel = () => { setEditing(null); setDraft(null); setNote(''); setSaveError(null); };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setDraftField = (k: string, v: any) => setDraft((d: any) => ({ ...(d || {}), [k]: v }));

  const save = async () => {
    const e = editing;
    if (!e || !detail) return;
    const needsNote = NOTE_KINDS.includes(e.kind);
    if (needsNote && !note.trim()) return;
    if (!currentUserId) { setSaveError('Could not identify the current user — please sign in again.'); return; }

    setSaving(true); setSaveError(null);
    try {
      const up = detail.ultimateParent;
      let fromLabel = '';
      let toLabel = '';
      let logHistory = needsNote;

      if (e.kind === 'temp') {
        const column = TEMP_COLUMN[e.path!];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fromLabel = healthStatusMeta((detail as any)[e.path!]).label; toLabel = healthStatusMeta(draft).label;
        await updateCustomerHealth(up, { [column]: draft } as CustomerHealthPatch, currentUserId);
      } else if (e.kind === 'status') {
        const item = detail.actionItems.find((a) => a.id === e.actionId);
        fromLabel = item?.status || ''; toLabel = draft;
        await updateActionItemStatus(e.actionId!, draft);
      } else if (e.kind === 'text') {
        const d = draft || {};
        fromLabel = detail.performance === d.status ? 'Assessment revised' : healthStatusMeta(detail.performance).label + ' → ' + healthStatusMeta(d.status).label;
        toLabel = 'updated';
        await updateCustomerHealth(up, { performance: d.status, assessment_summary: d.summary }, currentUserId);
      } else if (e.kind === 'money') {
        const column = MONEY_COLUMN[e.path!];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fromLabel = money((detail.financials as any)[e.path!]); toLabel = money(Number(draft) || 0);
        await updateCustomerHealth(up, { [column]: Number(draft) || 0 } as CustomerHealthPatch, currentUserId);
      } else if (e.kind === 'newAction') {
        const d = draft || {};
        await insertActionItem(up, { title: d.title.trim(), owner_id: (d.owner || '').trim() || null, status: d.status || 'Open', tat_days: Number(d.tat) || 14 });
        fromLabel = '—'; toLabel = d.title.trim();
      } else if (e.kind === 'hero') {
        const d = draft || {};
        const changes: string[] = [];
        const corePatch: CustomerHealthPatch = {};
        (([['ae', 'AE', 'account_executive_id', 'accountExecutiveId'], ['csam', 'CSAM', 'cs_account_manager_id', 'csAccountManagerId'], ['sponsor', 'Sponsor', 'executive_sponsor_id', 'executiveSponsorId']] as const)).forEach(([k, lbl, column, teamKey]) => {
          const nv = (d[k] || '').trim();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const old = (detail.team as any)[teamKey];
          if (nv && nv !== old) {
            changes.push(lbl + ': ' + userDisplayName(byId, old) + ' → ' + userDisplayName(byId, nv));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (corePatch as any)[column] = nv;
          }
        });
        if (changes.length) await updateCustomerHealth(up, corePatch, currentUserId);
        fromLabel = 'Team'; toLabel = changes.length ? changes.join('; ') : 'no change';
      } else if (e.kind === 'product') {
        const d = draft || {};
        // Arbitrary key/value metrics -> the health_products.metrics jsonb. Blank rows are dropped.
        const metrics: Metric[] = (d.metrics || [])
          .map((m: Metric) => ({ label: (m.label || '').trim(), val: (m.val ?? '').toString().trim() }))
          .filter((m: Metric) => m.label !== '' || m.val !== '');
        await upsertProduct(up, e.productKey!, { health_status: d.temp, metrics });
        logHistory = false;
      } else if (e.kind === 'deal') {
        const d = draft || {};
        const dealInput = {
          brand_name: (d.brandName || '').trim(), bdo_id: (d.pitcher || '').trim() || null, products: d.products || [],
          amount: Number(d.amount) || 0, tat_days: Number(d.tat) || 30, confidence: Number(d.confidence) || 0,
          status: (d.status || 'Qualified') as DealStatus,
        };
        if (e.dealId == null) await insertDeal(up, dealInput);
        else await updateDeal(e.dealId, dealInput);
        logHistory = false;
      }

      if (logHistory) {
        await insertChangeLog(up, { field: e.label, from_value: fromLabel, to_value: toLabel, note: note.trim(), changed_by: currentUserId });
      }
      await load(true);
      setEditing(null); setDraft(null); setNote('');
    } catch (err: any) {
      setSaveError(err?.response?.errors?.[0]?.message || 'Failed to save changes. You may not have permission.');
    } finally {
      setSaving(false);
    }
  };

  const addActionItemComment = async (actionItemId: string, text: string) => {
    if (!currentUserId) throw new Error('Could not identify the current user — please sign in again.');
    if (!detail) return;
    const item = detail.actionItems.find((a) => a.id === actionItemId);
    await insertActionItemComment(actionItemId, text, currentUserId);
    await insertChangeLog(detail.ultimateParent, {
      field: item?.title || 'Action Item', from_value: null, to_value: null, note: text,
      changed_by: currentUserId, entry_type: 'comment', action_item_id: actionItemId,
    });
    await load(true);
  };

  /* ---------- states ---------- */
  if (loading) {
    return <main className="ch-main ch-fade"><div className="ch-card" style={{ padding: 32, textAlign: 'center', fontSize: 14, color: '#999' }}>Loading…</div></main>;
  }
  if (error) {
    return (
      <main className="ch-main ch-fade">
        <div onClick={back} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4a90e2', cursor: 'pointer', marginBottom: 14 }}>‹ Back to Portfolio</div>
        <div className="ch-card" style={{ padding: 32, textAlign: 'center', fontSize: 14, color: '#b01400' }}>{error}</div>
      </main>
    );
  }
  if (!detail) {
    return (
      <main className="ch-main ch-fade">
        <div onClick={back} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4a90e2', cursor: 'pointer', marginBottom: 14 }}>‹ Back to Portfolio</div>
        <div className="ch-card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', marginBottom: 6 }}>Ultimate parent not found</div>
          <div style={{ fontSize: 13, color: '#999' }}>“{ultimateParent}” doesn’t match any tracked ultimate parent, or you don’t have access to it.</div>
        </div>
      </main>
    );
  }

  const b = detail;
  const openDeals = openDealsOf(b);
  const perfMeta = healthStatusMeta(b.performance);
  const displayProducts = displayProductsFor(b);

  /* ---------- render helpers ---------- */
  const person = (roleLabel: string, id: string) => {
    const name = displayName(id);
    return (
      <div key={roleLabel} style={{ flex: '1 1 200px', minWidth: 180, display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', border: '1px solid #f0f0f0', borderRadius: 10, background: '#fafbfc' }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: avatarBg[roleLabel] || '#0b2e9a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flex: 'none' }}>{initialsOf(name)}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '.4px' }}>{roleLabel}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        </div>
      </div>
    );
  };

  const healthChip = (path: string, label: string, val: HealthStatus) => {
    const m = healthStatusMeta(val);
    return (
      <div key={path} style={{ background: 'rgba(255,255,255,.94)', border: '1px solid rgba(255,255,255,.6)', borderRadius: 10, padding: '10px 14px', minWidth: 132, borderTop: '3px solid ' + m.c }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</span>
          {canEditRecords && <span onClick={() => startEdit({ kind: 'temp', path, label: label + ' Health', initial: val })} style={{ fontSize: 11, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span>}
        </div>
        <Badge t={val} big />
      </div>
    );
  };

  const productCard = (p: DisplayProduct) => {
    const m = healthStatusMeta(p.status);
    const editCfg: EditConfig = {
      kind: 'product', productKey: p.key, label: 'Edit ' + p.name,
      initial: { temp: p.status, metrics: p.metrics.map((x) => ({ label: x.label, val: x.val })) },
    };
    return (
      <div key={p.key} style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, borderLeft: '3px solid ' + (p.tracked ? m.c : '#d0d0d0'), display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{p.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
            {p.tracked
              ? <Badge t={p.status} />
              : <span style={{ fontSize: 11, fontWeight: 600, color: '#999', background: '#f0f0f0', padding: '3px 9px', borderRadius: 6 }}>Not rated</span>}
            {canEditRecords && <span onClick={() => startEdit(editCfg)} style={{ fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span>}
          </div>
        </div>
        {p.metrics.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 26px' }}>
            {p.metrics.map((mt, i) => (
              <div key={i} style={{ minWidth: 70 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.15, wordBreak: 'break-word' }}>{mt.val || '—'}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>{mt.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>
            {canEditRecords ? 'No metrics yet — click Edit to add.' : 'No metrics recorded yet.'}
          </div>
        )}
        {(p.installedOn || (p.tracked && p.threshold && p.threshold !== '—')) && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: '#999', marginTop: 12 }}>
            {p.installedOn && <span>{'Installed since ' + shortDate(p.installedOn)}</span>}
            {p.tracked && p.threshold && p.threshold !== '—' && <span style={{ fontStyle: 'italic' }}>{'Threshold: ' + p.threshold}</span>}
          </div>
        )}
      </div>
    );
  };

  const actionRow = (a: ActionItem) => (
    <ActionItemRow
      key={a.id}
      item={a}
      onEdit={() => startEdit({ kind: 'status', actionId: a.id, label: 'Action Status — ' + a.title, initial: a.status })}
      canEditRecords={canEditRecords}
      displayName={displayName}
      onAddComment={canEditRecords ? (text: string) => addActionItemComment(a.id, text) : null}
    />
  );

  const finTile = (label: string, val: string, sub: string, temp: HealthStatus | null, onEdit: (() => void) | null, budget?: { val: string; onEdit: (() => void) | null }) => {
    const m = temp ? healthStatusMeta(temp) : null;
    return (
      <div key={label} style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, borderTop: m ? ('3px solid ' + m.c) : '3px solid #4a90e2' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
          {onEdit ? <span onClick={onEdit} style={{ fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span> : null}
        </div>
        <div style={{ fontSize: 23, fontWeight: 700, color: '#1a1a1a' }}>{val}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
          {temp ? <Dot t={temp} size={8} /> : null}
          <span style={{ fontSize: 12, color: '#666' }}>{sub}</span>
        </div>
        {budget ? (
          <div style={{ fontSize: 12, color: '#999', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{'Budget ' + budget.val}</span>
            {budget.onEdit && <span onClick={budget.onEdit} style={{ color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Set</span>}
          </div>
        ) : null}
      </div>
    );
  };

  const stages: DealStatus[] = ['Qualified', 'Proposal', 'Negotiation'];
  const byStage = stages.map((s) => { const ds = openDeals.filter((x) => x.status === s); return { stage: s, value: ds.reduce((a, x) => a + x.amount, 0), count: ds.length }; });
  const maxFunnel = Math.max(1, ...byStage.map((s) => s.value));

  const funnelRow = (s: { stage: DealStatus; value: number; count: number }) => {
    const m = dealStatusMeta(s.stage);
    return (
      <div key={s.stage}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{s.stage}</span>
            <span style={{ fontSize: 12, color: '#999' }}>{'· ' + s.count + ' deals'}</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{money(s.value)}</span>
        </div>
        <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: (s.value / maxFunnel * 100) + '%', height: '100%', background: m.c, borderRadius: 4 }} />
        </div>
      </div>
    );
  };

  const dealRow = (dl: Deal) => {
    const m = dealStatusMeta(dl.status);
    return (
      <div key={dl.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1.6, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{money(dl.amount)}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{(dl.brandName ? dl.brandName + ' · ' : '') + 'Pitching: ' + displayName(dl.pitcherId) + ' · TAT ' + dl.tatDays + 'd'}</div>
        </div>
        <div style={{ flex: 1.4, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {dl.products.map((pn) => <span key={pn} style={{ fontSize: 11, fontWeight: 500, color: '#333', border: '1px solid #e0e0e0', borderRadius: 6, padding: '2px 7px' }}>{pn}</span>)}
        </div>
        <div style={{ flex: 'none', width: 96 }}>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 3 }}>{'Confidence ' + dl.confidence + '%'}</div>
          <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: dl.confidence + '%', height: '100%', background: '#4a90e2', borderRadius: 3 }} />
          </div>
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, minWidth: 150, justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: m.tx, background: m.bg, padding: '4px 10px', borderRadius: 6 }}>{dl.status}</span>
          {canEditRecords && <span onClick={() => startEdit({ kind: 'deal', dealId: dl.id, label: 'Edit Deal', initial: { brandName: dl.brandName || '', pitcher: dl.pitcherId, products: dl.products.slice(), amount: dl.amount, tat: dl.tatDays, confidence: dl.confidence, status: dl.status } })} style={{ fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span>}
        </div>
      </div>
    );
  };

  const histEl = (hh: HistoryEntry, i: number) => {
    const isComment = hh.entryType === 'comment';
    return (
      <div key={hh.id} style={{ display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' }}>
        <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: hh.tone ? healthStatusMeta(hh.tone).c : '#4a90e2', flex: 'none', marginTop: 3 }} />
          {i < b.history.length - 1 ? <span style={{ width: 2, flex: 1, background: '#f0f0f0', marginTop: 4 }} /> : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isComment ? (
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{displayName(hh.userId) + ' commented on “' + hh.field + '”'}</div>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{hh.field}</div>
              <div style={{ fontSize: 12, color: '#666', margin: '2px 0 4px' }}>{hh.from + ' → ' + hh.to}</div>
            </>
          )}
          <div style={{ fontSize: 12, color: '#333', background: '#f8f9fa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 10px', lineHeight: 1.45, marginTop: isComment ? 4 : 0 }}>{hh.note}</div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 5 }}>{displayName(hh.userId) + ' · ' + shortTimestamp(hh.at)}</div>
        </div>
      </div>
    );
  };

  const emptyRow = (t: string) => <div style={{ fontSize: 13, color: '#999', padding: '10px 2px' }}>{t}</div>;

  const backlogPct = pctOf(b.financials.backlog, b.financials.annualBudget);
  const backlogTone = budgetToneFromBacklogPct(backlogPct);
  const forecastPct = pctOf(b.financials.forecast, b.financials.annualBudget);

  return (
    <main className="ch-main ch-fade" style={{ padding: '20px 32px 40px' }}>
      <div onClick={back} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4a90e2', cursor: 'pointer', marginBottom: 14 }}>‹ Back to Portfolio</div>

      <div style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.08)', marginBottom: 16, border: '1px solid #e0e0e0' }}>
        <div style={{ background: '#0b2e9a', padding: '22px 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 160% at 88% -30%, rgba(255,255,255,.10), transparent 55%)' }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: healthStatusMeta(b.trackStatus).c, boxShadow: '0 0 0 4px ' + healthStatusMeta(b.trackStatus).bg, flex: 'none' }} />
                <h1 style={{ margin: 0, fontSize: 25, fontWeight: 700, color: '#fff', letterSpacing: '-.3px' }}>{b.ultimateParent}</h1>
                {canEditRecords && (
                  <button onClick={() => startEdit({ kind: 'hero', label: 'Edit Team', initial: { ae: b.team.accountExecutiveId, csam: b.team.csAccountManagerId, sponsor: b.team.executiveSponsorId } })} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,.35)', background: 'rgba(255,255,255,.12)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Edit Team</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 11, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#dfe7ff', background: 'rgba(255,255,255,.12)', padding: '4px 10px', borderRadius: 6 }}>{b.channel}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#dfe7ff', background: 'rgba(255,255,255,.12)', padding: '4px 10px', borderRadius: 6 }}>{b.sitesAcquired + '/' + b.sitesTotal + ' Sites Acquired'}</span>
              </div>
              {!!b.updatedAt && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.65)', marginTop: 9 }}>
                  {'Last updated by ' + (b.updatedByName || 'Unknown') + ' · ' + shortTimestamp(b.updatedAt)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {healthChip('trackStatus', 'Track', b.trackStatus)}
              {healthChip('sentimentStatus', 'Sentiment', b.sentimentStatus)}
              {healthChip('budgetStatus', 'Budget', b.budgetStatus)}
            </div>
          </div>
        </div>
        <div style={{ background: '#fff', padding: '16px 24px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', width: '100%' }}>
            {person('Account Executive', b.team.accountExecutiveId)}
            {person('CS Account Manager', b.team.csAccountManagerId)}
            {person('Executive Sponsor', b.team.executiveSponsorId)}
          </div>
        </div>
      </div>

      <div className="ch-detail-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <section className="ch-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Overall Assessment</h2>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: perfMeta.tx, background: perfMeta.bg, padding: '5px 12px', borderRadius: 6 }}><Dot t={b.performance} size={7} />{perfMeta.label}</span>
            </div>
            <div style={{ background: '#f8f9fa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 14px', fontSize: 14, color: '#333', lineHeight: 1.5, marginBottom: 16, position: 'relative' }}>
              {b.assessmentSummary}
              {canEditRecords && <span onClick={() => startEdit({ kind: 'text', label: 'Overall Assessment', initial: { summary: b.assessmentSummary, status: b.performance } })} style={{ position: 'absolute', top: 9, right: 12, fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div className="ch-col-label" style={{ marginBottom: 0 }}>Action Items</div>
              {canEditRecords && <button className="ch-btn-ghost" onClick={() => startEdit({ kind: 'newAction', label: 'Add Action Item', initial: { title: '', owner: '', tat: 14, status: 'Open' } })}>+ Add Action Item</button>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {b.actionItems.length ? b.actionItems.map((a) => actionRow(a)) : emptyRow('No action items.')}
            </div>
          </section>

          <section className="ch-card" style={{ padding: 20 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Products &amp; Solution Efficacy</h2>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#999' }}>Every product installed at this parent's sites. Set each product's health and record whatever efficacy metrics matter — edits are saved to the health record.</p>
            <div className="ch-two-col">{displayProducts.length ? displayProducts.map((p) => productCard(p)) : emptyRow('No products installed at this parent yet.')}</div>
          </section>

          <section className="ch-card" style={{ padding: 20 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Backlog &amp; Revenue</h2>
            <div className="ch-three-col">
              {finTile('Total Backlog', money(b.financials.backlog), 'Open, unbilled · maintained here', backlogTone, canEditRecords ? () => startEdit({ kind: 'money', path: 'backlog', label: 'Total Backlog', initial: b.financials.backlog }) : null)}
              {finTile('Revenue YTD', money(b.revenueYtd), pctOf(b.revenueYtd, b.financials.annualBudget) + '% of annual budget', backlogTone, null, { val: money(b.financials.annualBudget), onEdit: canEditRecords ? () => startEdit({ kind: 'money', path: 'annualBudget', label: 'Annual Budget', initial: b.financials.annualBudget }) : null })}
              {finTile('Forecast', money(b.financials.forecast), forecastPct + '% of annual budget', forecastToneFromPct(forecastPct), canEditRecords ? () => startEdit({ kind: 'money', path: 'forecast', label: 'Revenue Forecast', initial: b.financials.forecast }) : null)}
            </div>
          </section>

          <section className="ch-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Pipeline &amp; Funnel</h2>
              {canEditRecords && <button className="ch-btn-ghost" onClick={() => startEdit({ kind: 'deal', dealId: null, label: 'Add Deal', initial: { brandName: '', pitcher: '', products: [], amount: '', tat: 30, confidence: 50, status: 'Qualified' } })}>+ Add Deal</button>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>{byStage.map((s) => funnelRow(s))}</div>
            <div className="ch-col-label">Open Deals</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {openDeals.length ? openDeals.map((dl) => dealRow(dl)) : emptyRow('No open deals in the pipeline.')}
            </div>
          </section>
        </div>

        <aside className="ch-card ch-history" style={{ padding: 20 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>Change History</h2>
          <p style={{ margin: '0 0 16px', fontSize: 12, color: '#999' }}>Sentiment, budget &amp; assessment edits are logged. Newest first.</p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {b.history.length ? b.history.map((hh, i) => histEl(hh, i)) : emptyRow('No changes logged yet.')}
          </div>
        </aside>
      </div>

      {editing && <EditModal
        editing={editing} draft={draft} note={note} saving={saving} saveError={saveError}
        users={users} brandOptions={brandOptions} brandOptionsLoading={brandOptionsLoading}
        parentName={b.ultimateParent}
        onCancel={cancel} onSave={save} onDraft={setDraft} onDraftField={setDraftField} onNote={setNote}
      />}
    </main>
  );
}

/* ============================ edit modal ============================ */
function EditModal({ editing, draft, note, saving, saveError, users, brandOptions, brandOptionsLoading, parentName, onCancel, onSave, onDraft, onDraftField, onNote }: {
  editing: EditConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  draft: any;
  note: string;
  saving: boolean;
  saveError: string | null;
  users: import('../../services/userService').UserData[];
  brandOptions: BrandData[] | null;
  brandOptionsLoading: boolean;
  parentName: string;
  onCancel: () => void;
  onSave: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDraft: (v: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDraftField: (k: string, v: any) => void;
  onNote: (v: string) => void;
}) {
  const e = editing;
  const d = draft || {};

  const fieldLabel = (t: string) => <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 6 }}>{t}</label>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const txtInput = (val: any, onCh: (e: any) => void, ph?: string, type?: string) => (
    <input value={val !== undefined && val !== null ? val : ''} placeholder={ph || ''} type={type || 'text'} onChange={onCh}
      style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontFamily: 'inherit', fontSize: 14, color: '#333', outline: 'none' }} />
  );
  const optBtn = (value: HealthStatus) => {
    const meta = healthStatusMeta(value); const active = draft === value;
    return (
      <button key={value} onClick={() => onDraft(value)} style={{ flex: 1, padding: '14px 8px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', border: '2px solid ' + (active ? meta.c : '#e0e0e0'), background: active ? meta.bg : '#fff', color: active ? meta.tx : '#666', fontWeight: 600, fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'all .15s ease' }}>
        <Dot t={value} size={14} />{meta.label}
      </button>
    );
  };
  const statusBtn = (value: ActionStatus) => {
    const m = actionStatusMeta(value); const active = draft === value;
    return <button key={value} onClick={() => onDraft(value)} style={{ padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, border: '2px solid ' + (active ? m.tx : '#e0e0e0'), background: active ? m.bg : '#fff', color: active ? m.tx : '#666', transition: 'all .15s ease' }}>{value}</button>;
  };
  const naStatusBtn = (value: ActionStatus) => {
    const m = actionStatusMeta(value); const active = d.status === value;
    return <button key={value} onClick={() => onDraftField('status', value)} style={{ padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, border: '2px solid ' + (active ? m.tx : '#e0e0e0'), background: active ? m.bg : '#fff', color: active ? m.tx : '#666', transition: 'all .15s ease' }}>{value}</button>;
  };
  const dealStatusBtn = (value: DealStatus) => {
    const m = dealStatusMeta(value); const active = d.status === value;
    return <button key={value} onClick={() => onDraftField('status', value)} style={{ padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, border: '2px solid ' + (active ? m.c : '#e0e0e0'), background: active ? m.bg : '#fff', color: active ? m.tx : '#666', transition: 'all .15s ease' }}>{value}</button>;
  };

  const control = () => {
    if (e.kind === 'hero') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>{fieldLabel('Account Executive')}<UserPicker value={d.ae} onChange={(id) => onDraftField('ae', id)} users={users} placeholder="Select AE..." /></div>
          <div>{fieldLabel('CS Account Manager')}<UserPicker value={d.csam} onChange={(id) => onDraftField('csam', id)} users={users} placeholder="Select CSAM..." /></div>
          <div>{fieldLabel('Executive Sponsor')}<UserPicker value={d.sponsor} onChange={(id) => onDraftField('sponsor', id)} users={users} placeholder="Select sponsor..." /></div>
        </div>
      );
    }
    if (e.kind === 'product') {
      const metrics: { label: string; val: string }[] = d.metrics || [];
      const setMetric = (i: number, key: 'label' | 'val', value: string) => {
        const arr = metrics.map((mm) => ({ ...mm }));
        arr[i] = { ...arr[i], [key]: value };
        onDraftField('metrics', arr);
      };
      const addMetric = () => onDraftField('metrics', [...metrics, { label: '', val: '' }]);
      const removeMetric = (i: number) => onDraftField('metrics', metrics.filter((_, idx) => idx !== i));
      const rowInput = (val: string, ph: string, onCh: (v: string) => void) => (
        <input value={val} placeholder={ph} onChange={(ev) => onCh(ev.target.value)}
          style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 11px', fontFamily: 'inherit', fontSize: 14, color: '#333', outline: 'none' }} />
      );
      const productHealthBtn = (value: HealthStatus) => {
        const meta = healthStatusMeta(value); const active = d.temp === value;
        return (
          <button key={value} onClick={() => onDraftField('temp', value)} style={{ flex: 1, padding: '11px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, border: '2px solid ' + (active ? meta.c : '#e0e0e0'), background: active ? meta.bg : '#fff', color: active ? meta.tx : '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all .15s ease' }}>
            <Dot t={value} size={9} />{meta.label}
          </button>
        );
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>{fieldLabel('Health')}<div style={{ display: 'flex', gap: 10 }}>{(['healthy', 'watch', 'critical'] as HealthStatus[]).map((s) => productHealthBtn(s))}</div></div>
          <div>
            {fieldLabel('Metrics')}
            {metrics.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {metrics.map((mt, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1.2 }}>{rowInput(mt.label, 'Metric name (e.g. Uptime)', (v) => setMetric(i, 'label', v))}</div>
                    <div style={{ flex: 1 }}>{rowInput(mt.val, 'Value (e.g. 98%)', (v) => setMetric(i, 'val', v))}</div>
                    <button onClick={() => removeMetric(i)} title="Remove metric" style={{ flex: 'none', width: 34, height: 34, borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#b01400', fontSize: 18, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit' }}>×</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic', marginBottom: 8 }}>No metrics yet. Add one below.</div>
            )}
            <button onClick={addMetric} style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, border: '1px dashed #4a90e2', background: '#fff', color: '#4a90e2', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add metric</button>
          </div>
          <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>Not logged to change history.</div>
        </div>
      );
    }
    if (e.kind === 'deal') {
      const sel: string[] = d.products || [];
      const toggle = (name: string) => { const s = sel.includes(name) ? sel.filter((x) => x !== name) : [...sel, name]; onDraftField('products', s); };
      const brandNames = (brandOptions || []).map((bo) => bo.name);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>{fieldLabel('Brand')}
            <CustomDropdownWithSearch
              value={d.brandName ? [d.brandName] : []}
              options={brandNames}
              onChange={(selected) => onDraftField('brandName', selected[0] || '')}
              placeholder={brandOptionsLoading ? 'Loading brands…' : (brandNames.length ? 'Select brand...' : 'No brands found for this ultimate parent')}
              singleSelect
              showTags={false}
            />
          </div>
          <div>{fieldLabel('Pitching (owner)')}<UserPicker value={d.pitcher} onChange={(id) => onDraftField('pitcher', id)} users={users} placeholder="Select owner..." /></div>
          <div>{fieldLabel('Products')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ALL_PRODUCTS.map((n) => {
                const on = sel.includes(n);
                return <button key={n} onClick={() => toggle(n)} style={{ padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, border: '2px solid ' + (on ? '#4a90e2' : '#e0e0e0'), background: on ? '#eef4fc' : '#fff', color: on ? '#357abd' : '#666', transition: 'all .15s ease' }}>{n}</button>;
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1.4 }}>{fieldLabel('Total Amount (USD)')}{txtInput(d.amount, (ev) => onDraftField('amount', ev.target.value), '0', 'number')}</div>
            <div style={{ flex: 1 }}>{fieldLabel('TAT (days)')}{txtInput(d.tat, (ev) => onDraftField('tat', ev.target.value), '30', 'number')}</div>
            <div style={{ flex: 1 }}>{fieldLabel('Confidence %')}{txtInput(d.confidence, (ev) => onDraftField('confidence', ev.target.value), '0', 'number')}</div>
          </div>
          <div>{fieldLabel('Status')}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{(['Qualified', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'] as DealStatus[]).map((s) => dealStatusBtn(s))}</div></div>
        </div>
      );
    }
    if (e.kind === 'newAction') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>{fieldLabel('Action Item')}{txtInput(d.title, (ev) => onDraftField('title', ev.target.value), 'e.g. Restore Purchek uptime in Midwest region')}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 2 }}>{fieldLabel('Owner')}<UserPicker value={d.owner} onChange={(id) => onDraftField('owner', id)} users={users} placeholder="Select owner..." /></div>
            <div style={{ flex: 1 }}>{fieldLabel('TAT (days)')}{txtInput(d.tat, (ev) => onDraftField('tat', ev.target.value), '14', 'number')}</div>
          </div>
          <div>{fieldLabel('Status')}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{(['Open', 'In Progress', 'Blocked', 'Done'] as ActionStatus[]).map((s) => naStatusBtn(s))}</div></div>
        </div>
      );
    }
    if (e.kind === 'money') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#999' }}>$</span>
          <input value={draft !== undefined && draft !== null ? draft : ''} type="number" min={0} onChange={(ev) => onDraft(ev.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, color: '#1a1a1a', background: 'transparent' }} />
        </div>
      );
    }
    if (e.kind === 'temp') {
      return <div style={{ display: 'flex', gap: 10 }}>{optBtn('healthy')}{optBtn('watch')}{optBtn('critical')}</div>;
    }
    if (e.kind === 'status') {
      return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{(['Open', 'In Progress', 'Blocked', 'Done'] as ActionStatus[]).map((s) => statusBtn(s))}</div>;
    }
    if (e.kind === 'text') {
      const perfBtn = (value: HealthStatus) => {
        const m = healthStatusMeta(value); const active = d.status === value;
        return <button key={value} onClick={() => onDraftField('status', value)} style={{ flex: 1, padding: '11px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, border: '2px solid ' + (active ? m.c : '#e0e0e0'), background: active ? m.bg : '#fff', color: active ? m.tx : '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all .15s ease' }}><Dot t={value} size={9} />{m.label}</button>;
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>{fieldLabel('Performance')}<div style={{ display: 'flex', gap: 10 }}>{perfBtn('healthy')}{perfBtn('watch')}{perfBtn('critical')}</div></div>
          <div>{fieldLabel('Assessment')}
            <textarea value={d.summary || ''} onChange={(ev) => onDraftField('summary', ev.target.value)}
              style={{ width: '100%', minHeight: 120, resize: 'vertical', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, fontFamily: 'inherit', fontSize: 14, color: '#333', outline: 'none', lineHeight: 1.5 }} />
          </div>
        </div>
      );
    }
    return null;
  };

  const showNote = NOTE_KINDS.includes(e.kind);
  let valid = true;
  if (e.kind === 'newAction') valid = !!(d.title && d.title.trim());
  else if (e.kind === 'deal') valid = !!(d.pitcher && d.pitcher.trim()) && Number(d.amount) > 0;
  else if (e.kind === 'money') valid = draft !== '' && draft !== null && draft !== undefined && !isNaN(Number(draft));
  const canSave = valid && (!showNote || !!note.trim()) && !saving;

  return (
    <div className="ch-overlay" onClick={onCancel}>
      <div className="ch-modal ch-pop" style={{ width: (e.kind === 'deal' || e.kind === 'hero') ? 520 : 460 }} onClick={(ev) => ev.stopPropagation()}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{'Edit · ' + parentName}</div>
        <h3 style={{ margin: '0 0 18px', fontSize: 19, fontWeight: 700, color: '#1a1a1a' }}>{e.label}</h3>
        {control()}
        {showNote && (
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#333', margin: '20px 0 6px' }}>Notes <span style={{ color: '#ff6b35' }}>*</span></label>
            <textarea value={note} onChange={(ev) => onNote(ev.target.value)} placeholder="Document what changed and why (required for the audit trail)…"
              style={{ width: '100%', minHeight: 84, resize: 'vertical', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, fontFamily: 'inherit', fontSize: 14, color: '#333', outline: 'none' }} />
            <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{note.trim() ? 'This note will be attached to the change record.' : 'A note is required before you can save.'}</div>
          </div>
        )}
        {saveError && (
          <div style={{ fontSize: 13, color: '#b01400', background: 'rgba(225,25,0,.08)', borderRadius: 8, padding: '10px 12px', marginTop: 16 }}>{saveError}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button onClick={onCancel} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#666', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={onSave} disabled={!canSave} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: canSave ? '#ff6b35' : '#ccc', color: '#fff', fontSize: 14, fontWeight: 600, cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'inherit', transition: 'background .2s ease' }}>
            {saving ? 'Saving…' : (e.kind === 'deal' && e.dealId == null) ? 'Add Deal' : e.kind === 'newAction' ? 'Add Action Item' : !showNote ? 'Save' : 'Save Change'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BrandHealthDetail;
